import { Table, Pagination, DropdownButton, Dropdown } from "react-bootstrap";
import {
  flexRender,
  type Table as ReactTable,
  type Header,
} from "@tanstack/react-table";
import { SortOrderIcon } from "./SortOrderIcon";

function toPixels(value: number | undefined) {
  return value ? `${value}px` : undefined;
}

export function DataTable<T>({
  table,
  itemName = "item",
}: {
  table: ReactTable<T>;
  itemName?: string;
}) {
  // Generate a string for grid-template-columns based on table.getAllColumns()
  const templateColumns = table
    .getAllColumns()
    .map((c) => {
      // width property exists for typescript check
      if (c.columnDef.meta && "width" in c.columnDef.meta) {
        return c.columnDef.meta?.width;
      }

      if (c.columnDef.maxSize || c.columnDef.minSize) {
        const size = c.columnDef.size;
        return `minmax(${toPixels(c.columnDef.minSize || size)}, ${toPixels(c.columnDef.maxSize || size || Number.MAX_SAFE_INTEGER)})`;
      }
      return toPixels(c.columnDef.size) ?? "1fr";
    })
    .join(" ");

  return (
    <>
      <Table
        striped
        bordered
        responsive="xl"
        className="w-100 d-grid gap-0"
        style={{
          gridTemplateColumns: templateColumns,
        }}
      >
        <thead style={{ display: "contents" }}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              key={headerGroup.id}
              className="sticky-top d-grid"
              style={{
                gridColumn: "1 / -1",
                gridTemplateColumns: "subgrid",
              }}
            >
              {headerGroup.headers.map((header) => (
                <TableHeader header={header} key={header.id} />
              ))}
            </tr>
          ))}
        </thead>
        <tbody style={{ display: "contents" }}>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className="d-grid"
              style={{
                gridColumn: "1 / -1",
                gridTemplateColumns: "subgrid",
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className="text-truncate"
                  title={cell.getValue<string>()}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}

          {table.getState().pagination.pageSize < 50 &&
            table.getRowModel().rows.length <
              table.getState().pagination.pageSize &&
            Array.from(
              {
                length:
                  table.getState().pagination.pageSize -
                  table.getRowModel().rows.length,
              },
              (_, i) => (
                <tr
                  key={"blank_row_" + i}
                  className="d-grid"
                  style={{
                    gridColumn: "1 / -1",
                    gridTemplateColumns: "subgrid",
                  }}
                >
                  {Array.from({ length: table.getAllColumns().length }).map(
                    (_, j) => (
                      <td key={"blank_cell_" + j}>&nbsp;</td>
                    )
                  )}
                </tr>
              )
            )}
        </tbody>
      </Table>
      <footer className="d-flex justify-content-center justify-content-sm-between justify-content-sm-center justify-content-xxl-center align-items-center column-gap-4 row-gap-2 mt-2 flex-column flex-sm-row bg-white p-2 sticky-bottom">
        <span
          className="text-nowrap"
          style={{ gridArea: "left", justifySelf: "start" }}
        >
          {table.getRowCount()} total {itemName}s
        </span>
        <Pagination className="mb-0">
          <Pagination.First
            onClick={() => table.firstPage()}
            disabled={!table.getCanPreviousPage()}
          />
          <Pagination.Prev
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          />
          <Pagination.Item active className="text-nowrap">
            Page{" "}
            <span className="fw-bold">
              {table.getState().pagination.pageIndex + 1}
            </span>{" "}
            / {table.getPageCount()}
          </Pagination.Item>
          <Pagination.Next
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          />
          <Pagination.Last
            onClick={() => table.lastPage()}
            disabled={!table.getCanNextPage()}
          />
        </Pagination>
        <DropdownButton
          variant="secondary"
          size="sm"
          title={`Show ${table.getState().pagination.pageSize} ${itemName}s`}
        >
          {[5, 10, 15, 50, 100, 1000].map((pageSize) => (
            <Dropdown.Item
              key={pageSize}
              eventKey={pageSize}
              onClick={() => {
                table.setPageSize(pageSize);
              }}
              disabled={pageSize > table.getRowCount()}
            >
              Show {pageSize} {itemName}s
            </Dropdown.Item>
          ))}
        </DropdownButton>
      </footer>
    </>
  );
}

export function TableHeader<TData>({
  header,
}: {
  header: Header<TData, unknown>;
}) {
  return (
    <th
      key={header.id}
      onClick={
        header.column.getCanSort()
          ? header.column.getToggleSortingHandler()
          : undefined
      }
      className={`p-0 align-middle`}
      style={header.column.getCanSort() ? { cursor: "pointer" } : undefined}
      role="columnheader"
      title={
        header.column.getCanSort()
          ? `Click to sort by ${header.column.id} ${
              header.column.getIsSorted() === "asc"
                ? "descending"
                : header.column.getIsSorted() === "desc"
                  ? "unsorted"
                  : "ascending"
            }`
          : undefined
      }
    >
      <div className="d-flex align-items-center justify-content-between w-100 h-100 px-2">
        <span>
          {flexRender(header.column.columnDef.header, header.getContext())}
        </span>
        {header.column.getCanSort() && (
          <SortOrderIcon
            className="text-secondary"
            order={header.column.getIsSorted() || "none"}
          />
        )}
      </div>
    </th>
  );
}
